/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PrinterOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Popconfirm,
  Space,
  Table,
  TableColumnsType,
  TablePaginationConfig,
  Empty,
  Dropdown,
  MenuProps,
  Modal,
  Tabs,
  Card,
} from "antd";
import { NumericFormat } from "react-number-format";
import { Box, ChevronRight, Package, Calendar, History } from "lucide-react";
import { FC, useState, useEffect, useRef } from "react";
import {
  INVENTORY_KEYS,
  useDeleteInventory,
  useCreateInventory,
  SortByField,
} from "~/services/inventory";
import { TInventory } from "~/types";
import { capitalize, cn, formatPackingType } from "~/utils";
import {
  InventoryStatusCode,
  InventoryType,
  SortOrder,
} from "~/utils/constants";
import { useInventoryStore } from "../inventory.store";
import { useNotification } from "~/hooks";
import { PrintLabels } from "../../dispatch/print-labels/print-label";
import dayjs from "dayjs";
import UpcomingServiceTickets from "./upcoming-service-tickets";
import HistoryServiceTickets from "./history-service-tickets";
import { useGetHistoryServiceTicketsByInventoryId, useGetUpcomingServiceTicketsByInventoryId } from "~/services/service-ticket";

type Props = {
  inventories?: TInventory[];
  pagination: TablePaginationConfig;
};

export const TableInventory: FC<Props> = ({ inventories, pagination }) => {
  const { openInventoryModal, setFilters, filters } = useInventoryStore();
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const [inventoryToPrint, setInventoryToPrint] = useState<TInventory | null>(
    null
  );
  const [duplicateModalVisible, setDuplicateModalVisible] = useState(false);
  const [inventoryToDuplicate, setInventoryToDuplicate] =
    useState<TInventory | null>(null);
  const { error, success } = useNotification();
  const printTriggerRef = useRef<(() => void) | null>(null);
  const queryClient = useQueryClient();
  const { mutate: createInventory, isPending: isDuplicating } =
    useCreateInventory();

  const handleExpand = (record: TInventory) => {
    const key = record.id;
    setExpandedRowKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const showDuplicateModal = (record: TInventory) => {
    console.log("Selected inventory for duplication:", record);
    setInventoryToDuplicate(record);
    setDuplicateModalVisible(true);
  };

  const handleDuplicateConfirm = () => {
    if (!inventoryToDuplicate) {
      error({
        message: "No inventory selected",
        description: "Please select an inventory item to duplicate.",
      });
      return;
    }
    console.log("Duplicating inventory:", inventoryToDuplicate);
    const {
      id,
      inventoryRef,
      createdAt,
      updatedAt,
      containedInventories,
      client,
      location,
      parentInventory,
      ...rest
    } = inventoryToDuplicate;
    const payload = {
      ...rest,
      title: `${rest.title || "Untitled"} (Copy)`,
      clientId: client?.id || null,
      locationId: location?.id || null,
      parentInventoryId: parentInventory?.id || null,
      containedInventories: [],
    };
    console.log("Duplicate payload:", payload);
    createInventory(payload, {
      onSuccess: () => {
        success({
          message: "Inventory duplicated",
          description: "The inventory item was successfully duplicated.",
        });
        queryClient.invalidateQueries({
          queryKey: INVENTORY_KEYS.GET_INVENTORIES({}),
        });
        setDuplicateModalVisible(false);
        setInventoryToDuplicate(null);
      },
      onError: (err) => {
        console.error("Duplicate error:", err);
        error({
          message: "Failed to duplicate inventory",
          description:
            err.message ||
            "An error occurred while duplicating the inventory item.",
        });
        setDuplicateModalVisible(false);
        setInventoryToDuplicate(null);
      },
    });
  };

  const handleDuplicateCancel = () => {
    setDuplicateModalVisible(false);
    setInventoryToDuplicate(null);
  };

  useEffect(() => {
    if (inventoryToPrint && printTriggerRef.current) {
      try {
        printTriggerRef.current();
      } catch (err) {
        console.error("Error triggering print:", err);
        error({
          message: "Print failed",
          description: "Unable to open print dialog. Please try again.",
        });
      }
    }
  }, [inventoryToPrint, error]);

  const handleTableChange = (
    _pagination: TablePaginationConfig,
    _filters: any,
    sorter: any
  ) => {
    console.log("TableChange:", {
      _pagination,
      sorter,
      currentFilters: filters,
    }); // Debug log
    const newPage = _pagination.current || filters.page;
    const newPageSize = _pagination.pageSize || filters.pageSize;

    let sortBy: SortByField = filters.sortBy || SortByField.INVENTORY_REF;
    let sortOrder: SortOrder = filters.sortOrder || SortOrder.ASC;

    if (sorter.order) {
      sortOrder = sorter.order === "ascend" ? SortOrder.ASC : SortOrder.DESC;
      switch (sorter.field) {
        case "inventoryRef":
          sortBy = SortByField.INVENTORY_REF;
          break;
        case "artistName":
          sortBy = SortByField.ARTIST_NAME;
          break;
        case "title":
          sortBy = SortByField.TITLE;
          break;
        default:
          sortBy = SortByField.INVENTORY_REF;
      }
      setFilters({
        ...filters,
        sortBy,
        sortOrder,
        page: newPage,
        pageSize: newPageSize,
      });
    } else {
      setFilters({ ...filters, page: newPage, pageSize: newPageSize });
    }
  };

  const columns: TableColumnsType<TInventory> = [
  {
    title: "Pkg #",
    dataIndex: "inventoryRef",
    key: "inventoryRef",
    width: "100px",
    sorter: true,
    sortOrder:
      filters.sortBy === "inventoryRef"
        ? filters.sortOrder === SortOrder.ASC
          ? "ascend"
          : "descend"
        : undefined,
    render: (inventoryRef, record) => (
      <div className="flex items-center">
        
        <div className="text-sm font-mono">
          {inventoryRef || "-"}
        </div>
      </div>
    ),
  },
  {
    title: "Client",
    dataIndex: "client",
    key: "client",
    sorter: true,
    sortOrder:
      filters.sortBy === "client"
        ? filters.sortOrder === SortOrder.ASC
          ? "ascend"
          : "descend"
        : undefined,
    render: (client: any) => (
      <div>
        <div className="font-medium">
          {client?.companyName || (client ? `${client.firstName || ''} ${client.lastName || ''}`.trim() : 'N/A')}
        </div>
        
      </div>
    ),
  },
  {
    title: "Client Ref",
    dataIndex: "clientInternalRef",
    key: "clientInternalRef",
    width: "120px",
    align: "center",
    render: (clientInternalRef) => (
      <div className="text-sm">
        {clientInternalRef || "-"}
      </div>
    ),
  },
  {
    title: "Title",
    dataIndex: "title", 
    key: "title",
    width: "250px", // Wider for more text
    sorter: true,
    sortOrder:
      filters.sortBy === "title"
        ? filters.sortOrder === SortOrder.ASC
          ? "ascend"
          : "descend"
        : undefined,
    render: (title) => (
      <div className="font-medium">{title}</div>
    ),
  },
  {
    title: "Artist",
    dataIndex: "artistName",
    key: "artistName",
    sorter: true,
    sortOrder:
      filters.sortBy === "artistName"
        ? filters.sortOrder === SortOrder.ASC
          ? "ascend"
          : "descend"
        : undefined,
    render: (artistName) => artistName || "-",
  },
  {
    title: "Type",
    dataIndex: "itemType",
    key: "type",
    width: "100px",
    render: (type: InventoryType) => {
      const Icon = type === InventoryType.PACKAGE ? Box : Package;
      return (
        <div className="flex items-center">
          <Icon
            size={16}
            className={cn(
              type === InventoryType.PACKAGE
                ? "text-amber-500"
                : "text-green-500",
              "mr-1"
            )}
          />
          <span>{capitalize(type)}</span>
        </div>
      );
    },
  },
  {
    title: "Size (ft³)",
    key: "size",
    width: "100px",
    align: "center",
    render: (_, record) => (
      <NumericFormat
        type="text"
        displayType="text"
        decimalScale={5}
        value={(record.length * record.width * record.height) / 1728}
      />
    ),
  },
  {
    title: "Dimensions (L×D×H)",
    key: "dimensions",
    width: "140px",
    align: "center",
    render: (_, record) =>
      `${record.length ?? 0}" × ${record.width ?? 0}" × ${record.height ?? 0}"`,
  },
  {
    title: "Packing Type",
    key: "packingType",
    width: "140px",
    render: (_, record) => {
      const label = formatPackingType(record);
      if (label === "N/A") return "-";
      return (
        <div className="flex flex-col">
          <span className="text-sm">{label}</span>
        </div>
      );
    },
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    width: "120px",
    render: (status) => (
      <span
        className={cn(
          "px-2 py-1 rounded-full text-xs",
          status === InventoryStatusCode.AVAILABLE_ON_HAND
            ? "bg-green-100 text-green-800"
            : status === InventoryStatusCode.AVAILABLE_LIVE
              ? "bg-yellow-100 text-yellow-800"
              : status === InventoryStatusCode.RELEASED
                ? "bg-purple-100 text-purple-800"
                : status === InventoryStatusCode.NO_POSSESSION
                  ? "bg-orange-100 text-orange-800"
                  : "bg-gray-100 text-gray-800"
        )}
      >
        {capitalize(status.replace(/_/g, " "))}
      </span>
    ),
  },
  {
    title: "Location",
    dataIndex: ["location", "name"],
    key: "location",
    width: "120px",
    render: (name) => name || "-",
  },
  {
    title: "Action",
    key: "x",
    width: "100px",
    align: "center",
    render: (_, record) => {
      const items: MenuProps["items"] = [
        {
          key: "edit",
          label: "Edit",
          icon: <EditOutlined />,
          onClick: () => openInventoryModal(record.id),
        },
        {
          key: "print",
          label: "Print Label",
          icon: <PrinterOutlined />,
          onClick: () => setInventoryToPrint(record),
        },
        {
          key: "duplicate",
          label: "Duplicate",
          icon: <CopyOutlined />,
          onClick: () => showDuplicateModal(record),
        },
      ];

      return (
        <Space size="small">
          <Button
            type="text"
            icon={<EyeOutlined />}
            size="small"
            title="View"
            onClick={(e) => {
              e.stopPropagation();
              openInventoryModal(record.id, true);
            }}
          />
          <Dropdown menu={{ items }} trigger={["click"]}>
            <Button
              type="text"
              size="small"
              onClick={(e) => e.stopPropagation()}
              title="More Actions"
            >
              <span className="text-gray-500">⋯</span>
            </Button>
          </Dropdown>
          <DeleteInventoryButton inventoryId={record.id} />
        </Space>
      );
    },
  },
];

  return (
    <>
      <Table
        columns={columns}
        dataSource={inventories}
        rowKey="id"
        pagination={pagination}
        expandable={{
          expandedRowClassName: "[&>.ant-table-cell]:p-0",
          expandedRowRender: (record) => <CrateItem record={record} />,
          expandedRowKeys,
          onExpand: (_, record) => handleExpand(record),
          expandIcon: ({ expanded, record }) => (
            <Button
              type="text"
              className="p-1"
              onClick={(e) => {
                e.stopPropagation();
                handleExpand(record);
              }}
            >
              <ChevronRight
                size={16}
                className={cn(
                  "transition-transform",
                  "text-blue-500",
                  expanded && "rotate-90"
                )}
              />
            </Button>
          ),
        }}
        onRow={(record) => ({
          onClick: () => handleExpand(record),
          className: "cursor-pointer hover:bg-gray-50",
        })}
        onChange={handleTableChange}
      />
      {inventoryToPrint && (
        <PrintLabels
          selectedInventories={[inventoryToPrint]}
          copiesPerLabel={1}
          handlePrintRef={(handlePrint) =>
            (printTriggerRef.current = handlePrint)
          }
        />
      )}
      <Modal
        title="Confirm Duplication"
        open={duplicateModalVisible}
        onOk={handleDuplicateConfirm}
        onCancel={handleDuplicateCancel}
        okText="Yes"
        cancelText="No"
        confirmLoading={isDuplicating}
      >
        <p>Do you want to create a duplicate of this inventory item?</p>
      </Modal>
    </>
  );
};

const CrateItem: FC<{ record: TInventory }> = ({ record }) => {
  const { openInventoryModal } = useInventoryStore();
  const { TabPane } = Tabs;

  // Fetch data for counts
  const { data: historyData } = useGetHistoryServiceTicketsByInventoryId(record.id);
  const { data: upcomingData } = useGetUpcomingServiceTicketsByInventoryId(record.id);

  const historyCount = historyData?.items?.length || 0;
  const upcomingCount = upcomingData?.items?.length || 0;

  return (
    <Card
      className="border-y-0 rounded-none"
      classNames={{ body: "rounded-none" }}
    >
      <Tabs
        defaultActiveKey="details"
        type="card"
        className="mb-4 [&>.ant-tabs-nav]:mb-0"
      >
        <TabPane tab="Details" key="details">
          <Card
            className="border-y-0 rounded-none"
            classNames={{ body: "rounded-none" }}
          >
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <span className="text-sm text-gray-500">Dimensions:</span>
                <span className="ml-2">
                  {record.length}" × {record.width}" × {record.height}"
                </span>
              </div>
              <div>
                <span className="text-sm text-gray-500">Weight:</span>
                <span className="ml-2">{record.weight || "-"} lbs</span>
              </div>
              <div>
                <span className="text-sm text-gray-500">Client Reference:</span>
                <span className="ml-2">{record.clientInternalRef || "-"}</span>
              </div>
              <div>
                <span className="text-sm text-gray-500">Created:</span>
                <span className="ml-2">
                  {dayjs(record.createdAt).format("YYYY-MM-DD")}
                  {record.createdByUser && (
                    <>
                      {" - "}
                      {record.createdByUser.firstName || record.createdByUser.first_name
                        ? `${record.createdByUser.firstName || record.createdByUser.first_name} ${record.createdByUser.lastName || record.createdByUser.last_name || ""}`.trim()
                        : record.createdByUser.username || "Unknown"}
                      {record.createdByClient && (
                        <span className="ml-2 text-xs px-2 py-0.5 bg-blue-100 text-blue-800 rounded border border-blue-300">
                          Client
                        </span>
                      )}
                    </>
                  )}
                  {!record.createdByUser && (
                    <span className="ml-2 text-xs text-gray-500 italic">
                      (Unknown creator)
                    </span>
                  )}
                </span>
              </div>
              <div>
                <span className="text-sm text-gray-500">Last Updated:</span>
                <span className="ml-2">
                  {dayjs(record.updatedAt).format("YYYY-MM-DD")}
                </span>
              </div>
            </div>

            {record.notes && (
              <div className="mb-4">
                <div className="text-sm text-gray-500 mb-1">Notes:</div>
                <div className="p-3 bg-gray-50 border rounded">
                  {record.notes}
                </div>
              </div>
            )}

            <div>
              <div className="text-sm font-medium mb-2 text-gray-600">
                Contained Items:
              </div>
              <div className="bg-white p-2 rounded border">
                {record.containedInventories?.length === 0 ? (
                  <Empty description="No contained items" />
                ) : (
                  <div className="space-y-2">
                    {record.containedInventories.map((item) => (
                      <div
                        key={item.id}
                        className="p-3 bg-white border rounded flex justify-between items-center"
                        onClick={() => openInventoryModal(item.id)}
                      >
                        <div className="flex items-center">
                          <Package size={16} className="text-green-500 mr-2" />
                          <div>
                            <div className="font-medium">
                              {item.title || "Untitled"}
                            </div>
                            <div className="text-xs text-gray-500">
                              {item.inventoryRef || "-"}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          <div className="text-sm">
                            {item.length ?? 0}" × {item.width ?? 0}" × {item.height ?? 0}"
                          </div>
                          <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              openInventoryModal(item.id);
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="mt-4 flex justify-end space-x-2">
              <Button
                type="default"
                size="small"
                onClick={() => openInventoryModal(record.id)}
              >
                Edit
              </Button>
            </div>
          </Card>
        </TabPane>
        <TabPane
          tab={
            <div className="flex items-center space-x-1">
              <Calendar size={14} />
              <span>Upcoming Service Tickets</span>
              <span className="text-gray-500">({upcomingCount})</span>
            </div>
          }
          key="upcoming"
        >
          <UpcomingServiceTickets inventoryId={record.id} />
        </TabPane>
        <TabPane
          tab={
            <div className="flex items-center space-x-1">
              <History size={14} />
              <span>History</span>
              <span className="text-gray-500">({historyCount})</span>
            </div>
          }
          key="history"
        >
          <HistoryServiceTickets inventoryId={record.id} />
        </TabPane>
      </Tabs>
    </Card>
  );
};

const DeleteInventoryButton: FC<{ inventoryId: string }> = ({
  inventoryId,
}) => {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const { mutate: deleteInventory, isPending } = useDeleteInventory();
  const { success, error } = useNotification();

  const showPopconfirm = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setOpen(true);
  };

  const handleOk = () => {
    deleteInventory(inventoryId, {
      onSuccess: () => {
        setOpen(false);
        queryClient.invalidateQueries({
          queryKey: INVENTORY_KEYS.GET_INVENTORIES({}),
          refetchType: "all",
        });
        success({
          message: "Inventory deleted",
          description: "The inventory item was successfully deleted.",
        });
      },
      onError: (err) => {
        error({
          message: "Failed to delete inventory",
          description:
            err.message ||
            "An error occurred while deleting the inventory item.",
        });
      },
    });
  };

  const handleCancel = () => {
    setOpen(false);
  };

  return (
    <Popconfirm
      title="Are you sure to delete this item?"
      description="This action cannot be undone."
      okText="Yes"
      cancelText="No"
      placement="leftTop"
      open={open}
      onConfirm={handleOk}
      okButtonProps={{ loading: isPending }}
      onCancel={handleCancel}
    >
      <Button
        type="text"
        icon={<DeleteOutlined className="text-red-400" />}
        size="small"
        onClick={showPopconfirm}
      />
    </Popconfirm>
  );
};
